import { useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { useProperties, usePropertyMutations, type Property } from "@/hooks/useProperties";
import { useLocations, useLocationMutations, type Location } from "@/hooks/useLocations";
import { useInvestments, useInvestmentMutations, type Investment } from "@/hooks/useInvestments";
import { ImageGalleryField, ImageUploadField } from "@/components/admin/ImageFields";
import { toast } from "@/hooks/use-toast";
import { Trash2, Edit, Plus, LogOut, Home, MapPin, TrendingUp, X } from "lucide-react";

type Tab = "properties" | "locations" | "investments";

const PROPERTY_CATEGORY_LABELS: Record<string, string> = {
  offplan: "Offplan",
  "under-construction": "Under Construction",
  ready: "Ready",
  furnished: "Furnished",
  unfurnished: "Unfurnished",
};

const SALE_CATEGORY_OPTIONS = [
  { value: "offplan", label: "Offplan" },
  { value: "under-construction", label: "Under Construction" },
  { value: "ready", label: "Ready" },
];

const RENT_CATEGORY_OPTIONS = [
  { value: "furnished", label: "Furnished" },
  { value: "unfurnished", label: "Unfurnished" },
];

const createEmptyProperty = (): Partial<Property> => ({
  title: "",
  location: "",
  location_id: null,
  price: "",
  bedrooms: 0,
  bathrooms: 0,
  area: "",
  features: [],
  image: "",
  gallery_images: [],
  description: "",
  parking_spaces: 0,
  year_built: "",
  listing_type: "sale",
  category: "ready",
  is_featured: false,
  status_label: "For Sale",
  category_label: "Ready",
});

const createEmptyLocation = (): Partial<Location> => ({
  name: "",
  area: "Nairobi",
  image: "",
  property_count: "0",
  slug: "",
  is_featured: false,
});

const createEmptyInvestment = (): Partial<Investment> => ({
  title: "",
  location: "",
  type: "",
  price: "",
  roi: "",
  completion: "",
  description: "",
  features: [],
  image: "",
  is_featured: false,
});

const withPropertyDefaults = (property?: Partial<Property>): Partial<Property> => ({
  ...createEmptyProperty(),
  ...property,
  features: property?.features ?? [],
  gallery_images: property?.gallery_images ?? [],
});

const toList = (value?: string[]) => value?.join(", ") ?? "";
const fromList = (value: string) => value.split(",").map((item) => item.trim()).filter(Boolean);
const getErrorMessage = (error: unknown) => error instanceof Error ? error.message : "Something went wrong";

const applyPropertyMeta = (property: Partial<Property>, updates: Partial<Property>) => {
  const next = { ...property, ...updates };
  const listingType = (updates.listing_type ?? next.listing_type ?? "sale") as string;
  const category = (updates.category ?? next.category ?? "ready") as string;

  return {
    ...next,
    status_label: updates.status_label ?? (listingType === "sale" ? "For Sale" : "For Rent"),
    category_label: updates.category_label ?? PROPERTY_CATEGORY_LABELS[category] ?? category,
  };
};

const AdminPage = () => {
  const { user, isAdmin, loading, signOut } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<Tab>("properties");

  if (loading) return <div className="min-h-screen flex items-center justify-center bg-background"><p className="text-foreground font-body">Loading...</p></div>;
  if (!user) return <Navigate to="/login" replace />;
  if (!isAdmin) return <div className="min-h-screen flex items-center justify-center bg-background"><p className="text-foreground font-body">Access denied. Admin only.</p></div>;

  const handleLogout = async () => {
    await signOut();
    navigate("/");
  };

  const tabs: { key: Tab; label: string; icon: typeof Home }[] = [
    { key: "properties", label: "Properties", icon: Home },
    { key: "locations", label: "Locations", icon: MapPin },
    { key: "investments", label: "Investments", icon: TrendingUp },
  ];

  return (
    <div className="min-h-screen bg-background">
      {/* Top bar */}
      <div className="bg-primary text-primary-foreground px-4 md:px-6 py-3 md:py-4 flex items-center justify-between">
        <div className="flex items-center gap-2 md:gap-4">
          <a href="/" className="font-display text-lg md:text-xl font-light">Wild Gates Admin</a>
        </div>
        <div className="flex items-center gap-2 md:gap-4">
          <span className="hidden sm:block font-body text-xs text-primary-foreground/70">{user.email}</span>
          <button onClick={handleLogout} className="flex items-center gap-1 font-body text-xs text-primary-foreground/70 hover:text-primary-foreground">
            <LogOut className="w-4 h-4" />
            <span className="hidden sm:inline">Logout</span>
          </button>
        </div>
      </div>

      <div className="flex flex-col md:flex-row">
        {/* Sidebar */}
        <div className="w-full md:w-56 md:flex-shrink-0 bg-card border-b md:border-b-0 md:border-r border-border p-2 md:p-4 md:min-h-[calc(100vh-56px)]">
          <nav className="flex gap-2 overflow-x-auto pb-1 md:block md:space-y-1 md:overflow-visible md:pb-0">
            {tabs.map((tab) => (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key)}
                className={`flex min-w-fit flex-shrink-0 items-center justify-center gap-2 whitespace-nowrap rounded-lg px-3 py-2 font-body text-xs transition-colors md:w-full md:justify-start md:gap-3 md:px-3 md:py-2.5 md:text-sm ${
                  activeTab === tab.key ? "bg-accent text-accent-foreground" : "text-muted-foreground hover:text-foreground hover:bg-muted"
                }`}
              >
                <tab.icon className="w-4 h-4 flex-shrink-0" />
                <span className="truncate">{tab.label}</span>
              </button>
            ))}
          </nav>
        </div>

        {/* Content */}
        <div className="flex-1 p-3 md:p-4">
          {activeTab === "properties" && <PropertiesPanel />}
          {activeTab === "locations" && <LocationsPanel />}
          {activeTab === "investments" && <InvestmentsPanel />}
        </div>
      </div>
    </div>
  );
};

/* ─── Properties Panel ─── */
const PropertiesPanel = () => {
  const { data: properties, isLoading } = useProperties();
  const { data: locations, isLoading: loadingLocations } = useLocations();
  const { create, update, remove } = usePropertyMutations();
  const [editing, setEditing] = useState<Partial<Property> | null>(null);
  const [showForm, setShowForm] = useState(false);

  const handleSave = async () => {
    if (!editing) return;
    try {
      if (editing.id) {
        await update.mutateAsync(editing as Property & { id: string });
      } else {
        await create.mutateAsync(editing as Omit<Property, "id" | "created_at" | "updated_at">);
      }
      toast({ title: editing.id ? "Property updated" : "Property created" });
      setEditing(null);
      setShowForm(false);
    } catch (err: unknown) {
      toast({ title: "Error", description: getErrorMessage(err), variant: "destructive" });
    }
  };

  const handleDelete = async (id: string) => {
    if (!confirm("Delete this property?")) return;
    try {
      await remove.mutateAsync(id);
      toast({ title: "Property deleted" });
    } catch (err: unknown) {
      toast({ title: "Error", description: getErrorMessage(err), variant: "destructive" });
    }
  };

  return (
    <div>
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 md:gap-4 mb-4">
        <h2 className="font-display text-xl md:text-2xl font-light text-foreground">Properties</h2>
        <button
          onClick={() => { setEditing(createEmptyProperty()); setShowForm(true); }}
          className="flex items-center gap-2 bg-accent text-accent-foreground px-3 md:px-4 py-2 rounded-lg font-body text-sm hover:bg-accent/90 w-full sm:w-auto justify-center"
        >
          <Plus className="w-4 h-4" /> <span className="hidden sm:inline">Add Property</span>
          <span className="sm:hidden">Add</span>
        </button>
      </div>

      {showForm && editing && (
        <PropertyForm
          property={editing}
          locations={locations ?? []}
          loadingLocations={loadingLocations}
          onChange={setEditing}
          onSave={handleSave}
          onCancel={() => { setShowForm(false); setEditing(null); }}
        />
      )}

      {isLoading ? <p className="text-muted-foreground font-body text-sm">Loading...</p> : (
        <div className="bg-card border border-border rounded-lg overflow-hidden">
          {/* Mobile card view */}
          <div className="block md:hidden">
            <div className="divide-y divide-border">
              {properties?.map((p) => (
                <div key={p.id} className="p-4">
                  <div className="flex justify-between items-start mb-2">
                    <h3 className="font-body text-sm font-medium text-foreground truncate flex-1 mr-2">{p.title}</h3>
                    <div className="flex gap-1 flex-shrink-0">
                      <button onClick={() => { setEditing(withPropertyDefaults(p)); setShowForm(true); }} className="text-accent hover:text-accent/80 p-1"><Edit className="w-4 h-4" /></button>
                      <button onClick={() => handleDelete(p.id)} className="text-destructive hover:text-destructive/80 p-1"><Trash2 className="w-4 h-4" /></button>
                    </div>
                  </div>
                  <div className="space-y-1 text-xs">
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Location:</span>
                      <span className="text-foreground">{p.location}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Type:</span>
                      <span className="bg-accent/10 text-accent px-2 py-1 rounded">{p.listing_type}/{p.category}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Price:</span>
                      <span className="text-foreground">{p.price}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Featured:</span>
                      <span className="text-foreground">{p.is_featured ? "⭐" : "—"}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Desktop table view */}
          <div className="hidden md:block">
            <table className="w-full">
              <thead className="bg-muted">
                <tr>
                  <th className="text-left px-4 py-3 font-body text-xs text-muted-foreground uppercase">Title</th>
                  <th className="text-left px-4 py-3 font-body text-xs text-muted-foreground uppercase">Location</th>
                  <th className="text-left px-4 py-3 font-body text-xs text-muted-foreground uppercase">Type</th>
                  <th className="text-left px-4 py-3 font-body text-xs text-muted-foreground uppercase">Price</th>
                  <th className="text-left px-4 py-3 font-body text-xs text-muted-foreground uppercase">Featured</th>
                  <th className="text-right px-4 py-3 font-body text-xs text-muted-foreground uppercase">Actions</th>
                </tr>
              </thead>
              <tbody>
                {properties?.map((p) => (
                  <tr key={p.id} className="border-t border-border">
                    <td className="px-4 py-3 font-body text-sm text-foreground">{p.title}</td>
                    <td className="px-4 py-3 font-body text-sm text-muted-foreground">{p.location}</td>
                    <td className="px-4 py-3 font-body text-xs"><span className="bg-accent/10 text-accent px-2 py-1 rounded">{p.listing_type}/{p.category}</span></td>
                    <td className="px-4 py-3 font-body text-sm text-foreground">{p.price}</td>
                    <td className="px-4 py-3 font-body text-sm">{p.is_featured ? "⭐" : "—"}</td>
                    <td className="px-4 py-3 text-right">
                      <button onClick={() => { setEditing(withPropertyDefaults(p)); setShowForm(true); }} className="text-accent hover:text-accent/80 mr-3"><Edit className="w-4 h-4" /></button>
                      <button onClick={() => handleDelete(p.id)} className="text-destructive hover:text-destructive/80"><Trash2 className="w-4 h-4" /></button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

const PropertyForm = ({
  property,
  locations,
  loadingLocations,
  onChange,
  onSave,
  onCancel,
}: {
  property: Partial<Property>;
  locations: Location[];
  loadingLocations: boolean;
  onChange: (p: Partial<Property>) => void;
  onSave: () => void;
  onCancel: () => void;
}) => {
  const current = withPropertyDefaults(property);
  const set = (updates: Partial<Property>) => onChange(applyPropertyMeta(current, updates));
  const [mainImageUploading, setMainImageUploading] = useState(false);
  const [galleryUploading, setGalleryUploading] = useState(false);
  const isUploading = mainImageUploading || galleryUploading;
  const categoryOptions = current.listing_type === "rent" ? RENT_CATEGORY_OPTIONS : SALE_CATEGORY_OPTIONS;

  return (
    <div className="bg-card border border-border rounded-lg p-4 md:p-6 mb-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-display text-lg text-foreground">{current.id ? "Edit" : "New"} Property</h3>
        <button onClick={onCancel}><X className="w-5 h-5 text-muted-foreground" /></button>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="space-y-2">
          <label className="block font-body text-xs font-medium uppercase tracking-[0.2em] text-muted-foreground">Property title</label>
          <input placeholder="Title" value={current.title || ""} onChange={(e) => set({ title: e.target.value })} className="w-full px-3 py-2 border border-border rounded-lg bg-background text-foreground font-body text-sm" />
        </div>
        <div className="space-y-2">
          <label className="block font-body text-xs font-medium uppercase tracking-[0.2em] text-muted-foreground">Linked location</label>
          <select
            value={current.location_id ?? ""}
            onChange={(e) => {
              const selected = locations.find((location) => location.id === e.target.value);
              set({ location_id: e.target.value || null, location: selected?.name ?? "" });
            }}
            disabled={loadingLocations || locations.length === 0}
            className="w-full px-3 py-2 border border-border rounded-lg bg-background text-foreground font-body text-sm disabled:cursor-not-allowed disabled:opacity-60"
          >
            <option value="">
              {loadingLocations ? "Loading locations..." : locations.length === 0 ? "No locations available" : "Select linked location"}
            </option>
            {locations.map((location) => (
              <option key={location.id} value={location.id}>{location.name}</option>
            ))}
          </select>
          <p className="font-body text-xs text-muted-foreground">
            Choose the location here so this property automatically appears on that location page.
          </p>
        </div>
        <input placeholder="Price (e.g. KES 45M)" value={current.price || ""} onChange={(e) => set({ price: e.target.value })} className="px-3 py-2 border border-border rounded-lg bg-background text-foreground font-body text-sm" />
        <input placeholder="Area (e.g. 1,800 sq ft)" value={current.area || ""} onChange={(e) => set({ area: e.target.value })} className="px-3 py-2 border border-border rounded-lg bg-background text-foreground font-body text-sm" />
        <input type="number" placeholder="Bedrooms" value={current.bedrooms || 0} onChange={(e) => set({ bedrooms: parseInt(e.target.value) || 0 })} className="px-3 py-2 border border-border rounded-lg bg-background text-foreground font-body text-sm" />
        <input type="number" placeholder="Bathrooms" value={current.bathrooms || 0} onChange={(e) => set({ bathrooms: parseInt(e.target.value) || 0 })} className="px-3 py-2 border border-border rounded-lg bg-background text-foreground font-body text-sm" />
        <input type="number" placeholder="Parking Spaces" value={current.parking_spaces || 0} onChange={(e) => set({ parking_spaces: parseInt(e.target.value) || 0 })} className="px-3 py-2 border border-border rounded-lg bg-background text-foreground font-body text-sm" />
        <input placeholder="Year Built" value={current.year_built || ""} onChange={(e) => set({ year_built: e.target.value })} className="px-3 py-2 border border-border rounded-lg bg-background text-foreground font-body text-sm" />
        <select
          value={current.listing_type || "sale"}
          onChange={(e) => {
            const listingType = e.target.value;
            const defaultCategory = listingType === "rent" ? "furnished" : "ready";
            set({ listing_type: listingType, category: defaultCategory });
          }}
          className="px-3 py-2 border border-border rounded-lg bg-background text-foreground font-body text-sm"
        >
          <option value="sale">For Sale</option>
          <option value="rent">For Rent</option>
        </select>
        <select value={current.category || "ready"} onChange={(e) => set({ category: e.target.value })} className="px-3 py-2 border border-border rounded-lg bg-background text-foreground font-body text-sm">
          {categoryOptions.map((option) => (
            <option key={option.value} value={option.value}>{option.label}</option>
          ))}
        </select>
        <input placeholder="Status Label (e.g. For Sale)" value={current.status_label || ""} onChange={(e) => set({ status_label: e.target.value })} className="px-3 py-2 border border-border rounded-lg bg-background text-foreground font-body text-sm" />
        <input placeholder="Category Label" value={current.category_label || ""} onChange={(e) => set({ category_label: e.target.value })} className="px-3 py-2 border border-border rounded-lg bg-background text-foreground font-body text-sm" />
        <ImageUploadField
          label="Featured image"
          value={current.image || ""}
          folder="properties/featured"
          onChange={(image) => set({ image })}
          onUploadingChange={setMainImageUploading}
        />
        <textarea placeholder="Description" value={current.description || ""} onChange={(e) => set({ description: e.target.value })} className="px-3 py-2 border border-border rounded-lg bg-background text-foreground font-body text-sm col-span-1 md:col-span-2" rows={4} />
        <input placeholder="Features (comma separated)" value={toList(current.features)} onChange={(e) => set({ features: fromList(e.target.value) })} className="px-3 py-2 border border-border rounded-lg bg-background text-foreground font-body text-sm col-span-1 md:col-span-2" />
        <ImageGalleryField
          label="Gallery images"
          value={current.gallery_images ?? []}
          folder="properties/gallery"
          className="col-span-1 md:col-span-2"
          onChange={(gallery_images) => set({ gallery_images })}
          onUploadingChange={setGalleryUploading}
        />
        <label className="flex items-center gap-2 font-body text-sm text-foreground col-span-1 md:col-span-2">
          <input type="checkbox" checked={current.is_featured || false} onChange={(e) => set({ is_featured: e.target.checked })} className="rounded" />
          Featured on homepage
        </label>
      </div>
      <div className="mt-4 rounded-lg border border-border bg-muted/30 p-4">
        <p className="font-body text-xs text-muted-foreground">
          {loadingLocations ? "Loading available locations..." : locations.length > 0 ? "Each property should be linked to a location so it automatically appears on that location page." : "Add at least one location first so properties can be linked to location pages."}
        </p>
      </div>
      <div className="mt-4 flex flex-col sm:flex-row gap-3">
        <button onClick={onSave} disabled={isUploading} className="bg-accent text-accent-foreground px-6 py-2 rounded-lg font-body text-sm hover:bg-accent/90 disabled:cursor-not-allowed disabled:opacity-60 w-full sm:w-auto">{isUploading ? "Uploading images..." : "Save"}</button>
        <button onClick={onCancel} className="border border-border px-6 py-2 rounded-lg font-body text-sm text-muted-foreground hover:text-foreground w-full sm:w-auto">Cancel</button>
      </div>
    </div>
  );
};

/* ─── Locations Panel ─── */
const LocationsPanel = () => {
  const { data: locations, isLoading } = useLocations();
  const { create, update, remove } = useLocationMutations();
  const [editing, setEditing] = useState<Partial<Location> | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [imageUploading, setImageUploading] = useState(false);

  const handleSave = async () => {
    if (!editing) return;
    try {
      if (editing.id) {
        await update.mutateAsync(editing as Location & { id: string });
      } else {
        await create.mutateAsync(editing as Omit<Location, "id" | "created_at" | "updated_at">);
      }
      toast({ title: editing.id ? "Location updated" : "Location created" });
      setEditing(null); setShowForm(false);
    } catch (err: unknown) {
      toast({ title: "Error", description: getErrorMessage(err), variant: "destructive" });
    }
  };

  const handleDelete = async (id: string) => {
    if (!confirm("Delete this location?")) return;
    try { await remove.mutateAsync(id); toast({ title: "Location deleted" }); } catch (err: unknown) { toast({ title: "Error", description: getErrorMessage(err), variant: "destructive" }); }
  };

  return (
    <div>
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 md:gap-4 mb-4">
        <h2 className="font-display text-xl md:text-2xl font-light text-foreground">Locations</h2>
        <button onClick={() => { setEditing(createEmptyLocation()); setShowForm(true); }} className="flex items-center gap-2 bg-accent text-accent-foreground px-3 md:px-4 py-2 rounded-lg font-body text-sm hover:bg-accent/90 w-full sm:w-auto justify-center">
          <Plus className="w-4 h-4" /> <span className="hidden sm:inline">Add Location</span>
          <span className="sm:hidden">Add</span>
        </button>
      </div>

      {showForm && editing && (
        <div className="bg-card border border-border rounded-lg p-4 md:p-6 mb-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-display text-lg text-foreground">{editing.id ? "Edit" : "New"} Location</h3>
            <button onClick={() => { setShowForm(false); setEditing(null); }}><X className="w-5 h-5 text-muted-foreground" /></button>
          </div>
          <div className="grid md:grid-cols-2 gap-4">
            <input placeholder="Name" value={editing.name || ""} onChange={(e) => setEditing({ ...editing, name: e.target.value })} className="px-3 py-2 border border-border rounded-lg bg-background text-foreground font-body text-sm" />
            <input placeholder="Area" value={editing.area || ""} onChange={(e) => setEditing({ ...editing, area: e.target.value })} className="px-3 py-2 border border-border rounded-lg bg-background text-foreground font-body text-sm" />
            <ImageUploadField
              label="Featured image"
              value={editing.image || ""}
              folder="locations"
              onChange={(image) => setEditing({ ...editing, image })}
              onUploadingChange={setImageUploading}
            />
            <label className="flex items-center gap-2 font-body text-sm text-foreground">
              <input type="checkbox" checked={editing.is_featured || false} onChange={(e) => setEditing({ ...editing, is_featured: e.target.checked })} className="rounded" />
              Featured on homepage
            </label>
            <div className="md:col-span-2 rounded-lg border border-border bg-muted/30 p-4">
              <p className="font-body text-xs text-muted-foreground">The slug and property count are managed automatically from the database. Renaming a location keeps linked properties in sync.</p>
              {editing.slug && <p className="font-body text-xs text-muted-foreground mt-2">Current slug: <span className="text-foreground">{editing.slug}</span></p>}
            </div>
          </div>
          <div className="mt-4 flex flex-col sm:flex-row gap-3">
            <button onClick={handleSave} disabled={imageUploading} className="bg-accent text-accent-foreground px-6 py-2 rounded-lg font-body text-sm hover:bg-accent/90 disabled:cursor-not-allowed disabled:opacity-60 w-full sm:w-auto">{imageUploading ? "Uploading image..." : "Save"}</button>
            <button onClick={() => { setShowForm(false); setEditing(null); }} className="border border-border px-6 py-2 rounded-lg font-body text-sm text-muted-foreground w-full sm:w-auto">Cancel</button>
          </div>
        </div>
      )}

      {isLoading ? <p className="text-muted-foreground font-body text-sm">Loading...</p> : (
        <div className="bg-card border border-border rounded-lg overflow-hidden">
          {/* Mobile card view */}
          <div className="block md:hidden">
            <div className="divide-y divide-border">
              {locations?.map((l) => (
                <div key={l.id} className="p-4">
                  <div className="flex justify-between items-start mb-2">
                    <h3 className="font-body text-sm font-medium text-foreground truncate flex-1 mr-2">{l.name}</h3>
                    <div className="flex gap-1 flex-shrink-0">
                      <button onClick={() => { setEditing(l); setShowForm(true); }} className="text-accent hover:text-accent/80 p-1"><Edit className="w-4 h-4" /></button>
                      <button onClick={() => handleDelete(l.id)} className="text-destructive hover:text-destructive/80 p-1"><Trash2 className="w-4 h-4" /></button>
                    </div>
                  </div>
                  <div className="space-y-1 text-xs">
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Area:</span>
                      <span className="text-foreground">{l.area}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Slug:</span>
                      <span className="text-foreground truncate">{l.slug}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Properties:</span>
                      <span className="text-foreground">{l.property_count}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Featured:</span>
                      <span className="text-foreground">{l.is_featured ? "⭐" : "—"}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Desktop table view */}
          <div className="hidden md:block">
            <table className="w-full">
              <thead className="bg-muted">
                <tr>
                  <th className="text-left px-4 py-3 font-body text-xs text-muted-foreground uppercase">Name</th>
                  <th className="text-left px-4 py-3 font-body text-xs text-muted-foreground uppercase">Area</th>
                  <th className="text-left px-4 py-3 font-body text-xs text-muted-foreground uppercase">Slug</th>
                  <th className="text-left px-4 py-3 font-body text-xs text-muted-foreground uppercase">Properties</th>
                  <th className="text-left px-4 py-3 font-body text-xs text-muted-foreground uppercase">Featured</th>
                  <th className="text-right px-4 py-3 font-body text-xs text-muted-foreground uppercase">Actions</th>
                </tr>
              </thead>
              <tbody>
                {locations?.map((l) => (
                  <tr key={l.id} className="border-t border-border">
                    <td className="px-4 py-3 font-body text-sm text-foreground">{l.name}</td>
                    <td className="px-4 py-3 font-body text-sm text-muted-foreground">{l.area}</td>
                    <td className="px-4 py-3 font-body text-sm text-muted-foreground">{l.slug}</td>
                    <td className="px-4 py-3 font-body text-sm text-foreground">{l.property_count}</td>
                    <td className="px-4 py-3 font-body text-sm">{l.is_featured ? "⭐" : "—"}</td>
                    <td className="px-4 py-3 text-right">
                      <button onClick={() => { setEditing(l); setShowForm(true); }} className="text-accent hover:text-accent/80 mr-3"><Edit className="w-4 h-4" /></button>
                      <button onClick={() => handleDelete(l.id)} className="text-destructive hover:text-destructive/80"><Trash2 className="w-4 h-4" /></button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

/* ─── Investments Panel ─── */
const InvestmentsPanel = () => {
  const { data: investments, isLoading } = useInvestments();
  const { create, update, remove } = useInvestmentMutations();
  const [editing, setEditing] = useState<Partial<Investment> | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [imageUploading, setImageUploading] = useState(false);

  const handleSave = async () => {
    if (!editing) return;
    try {
      if (editing.id) {
        await update.mutateAsync(editing as Investment & { id: string });
      } else {
        await create.mutateAsync(editing as Omit<Investment, "id" | "created_at" | "updated_at">);
      }
      toast({ title: editing.id ? "Investment updated" : "Investment created" });
      setEditing(null); setShowForm(false);
    } catch (err: unknown) {
      toast({ title: "Error", description: getErrorMessage(err), variant: "destructive" });
    }
  };

  const handleDelete = async (id: string) => {
    if (!confirm("Delete this investment?")) return;
    try { await remove.mutateAsync(id); toast({ title: "Investment deleted" }); } catch (err: unknown) { toast({ title: "Error", description: getErrorMessage(err), variant: "destructive" }); }
  };

  return (
    <div>
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 md:gap-4 mb-4">
        <h2 className="font-display text-xl md:text-2xl font-light text-foreground">Investments</h2>
        <button onClick={() => { setEditing(createEmptyInvestment()); setShowForm(true); }} className="flex items-center gap-2 bg-accent text-accent-foreground px-3 md:px-4 py-2 rounded-lg font-body text-sm hover:bg-accent/90 w-full sm:w-auto justify-center">
          <Plus className="w-4 h-4" /> <span className="hidden sm:inline">Add Investment</span>
          <span className="sm:hidden">Add</span>
        </button>
      </div>

      {showForm && editing && (
        <div className="bg-card border border-border rounded-lg p-4 md:p-6 mb-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-display text-lg text-foreground">{editing.id ? "Edit" : "New"} Investment</h3>
            <button onClick={() => { setShowForm(false); setEditing(null); }}><X className="w-5 h-5 text-muted-foreground" /></button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <input placeholder="Title" value={editing.title || ""} onChange={(e) => setEditing({ ...editing, title: e.target.value })} className="px-3 py-2 border border-border rounded-lg bg-background text-foreground font-body text-sm" />
            <input placeholder="Location" value={editing.location || ""} onChange={(e) => setEditing({ ...editing, location: e.target.value })} className="px-3 py-2 border border-border rounded-lg bg-background text-foreground font-body text-sm" />
            <input placeholder="Type (e.g. Luxury Apartments)" value={editing.type || ""} onChange={(e) => setEditing({ ...editing, type: e.target.value })} className="px-3 py-2 border border-border rounded-lg bg-background text-foreground font-body text-sm" />
            <input placeholder="Price" value={editing.price || ""} onChange={(e) => setEditing({ ...editing, price: e.target.value })} className="px-3 py-2 border border-border rounded-lg bg-background text-foreground font-body text-sm" />
            <input placeholder="ROI (e.g. 12-15% Annual)" value={editing.roi || ""} onChange={(e) => setEditing({ ...editing, roi: e.target.value })} className="px-3 py-2 border border-border rounded-lg bg-background text-foreground font-body text-sm" />
            <input placeholder="Completion (e.g. Q4 2025)" value={editing.completion || ""} onChange={(e) => setEditing({ ...editing, completion: e.target.value })} className="px-3 py-2 border border-border rounded-lg bg-background text-foreground font-body text-sm" />
            <input placeholder="Features (comma separated)" value={(editing.features || []).join(", ")} onChange={(e) => setEditing({ ...editing, features: e.target.value.split(",").map(f => f.trim()).filter(Boolean) })} className="px-3 py-2 border border-border rounded-lg bg-background text-foreground font-body text-sm" />
            <ImageUploadField
              label="Featured image"
              value={editing.image || ""}
              folder="investments"
              onChange={(image) => setEditing({ ...editing, image })}
              onUploadingChange={setImageUploading}
            />
            <textarea placeholder="Description" value={editing.description || ""} onChange={(e) => setEditing({ ...editing, description: e.target.value })} className="px-3 py-2 border border-border rounded-lg bg-background text-foreground font-body text-sm col-span-1 md:col-span-2" rows={3} />
            <label className="flex items-center gap-2 font-body text-sm text-foreground col-span-1 md:col-span-2">
              <input type="checkbox" checked={editing.is_featured || false} onChange={(e) => setEditing({ ...editing, is_featured: e.target.checked })} className="rounded" />
              Featured on homepage
            </label>
          </div>
          <div className="mt-4 flex flex-col sm:flex-row gap-3">
            <button onClick={handleSave} disabled={imageUploading} className="bg-accent text-accent-foreground px-6 py-2 rounded-lg font-body text-sm hover:bg-accent/90 disabled:cursor-not-allowed disabled:opacity-60 w-full sm:w-auto">{imageUploading ? "Uploading image..." : "Save"}</button>
            <button onClick={() => { setShowForm(false); setEditing(null); }} className="border border-border px-6 py-2 rounded-lg font-body text-sm text-muted-foreground w-full sm:w-auto">Cancel</button>
          </div>
        </div>
      )}

      {isLoading ? <p className="text-muted-foreground font-body text-sm">Loading...</p> : (
        <div className="bg-card border border-border rounded-lg overflow-hidden">
          {/* Mobile card view */}
          <div className="block md:hidden">
            <div className="divide-y divide-border">
              {investments?.map((inv) => (
                <div key={inv.id} className="p-4">
                  <div className="flex justify-between items-start mb-2">
                    <h3 className="font-body text-sm font-medium text-foreground truncate flex-1 mr-2">{inv.title}</h3>
                    <div className="flex gap-1 flex-shrink-0">
                      <button onClick={() => { setEditing(inv); setShowForm(true); }} className="text-accent hover:text-accent/80 p-1"><Edit className="w-4 h-4" /></button>
                      <button onClick={() => handleDelete(inv.id)} className="text-destructive hover:text-destructive/80 p-1"><Trash2 className="w-4 h-4" /></button>
                    </div>
                  </div>
                  <div className="space-y-1 text-xs">
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Location:</span>
                      <span className="text-foreground">{inv.location}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Type:</span>
                      <span className="text-foreground">{inv.type}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Price:</span>
                      <span className="text-foreground">{inv.price}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">ROI:</span>
                      <span className="text-foreground">{inv.roi}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Featured:</span>
                      <span className="text-foreground">{inv.is_featured ? "⭐" : "—"}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Desktop table view */}
          <div className="hidden md:block">
            <table className="w-full">
              <thead className="bg-muted">
                <tr>
                  <th className="text-left px-4 py-3 font-body text-xs text-muted-foreground uppercase">Title</th>
                  <th className="text-left px-4 py-3 font-body text-xs text-muted-foreground uppercase">Location</th>
                  <th className="text-left px-4 py-3 font-body text-xs text-muted-foreground uppercase">Type</th>
                  <th className="text-left px-4 py-3 font-body text-xs text-muted-foreground uppercase">Price</th>
                  <th className="text-left px-4 py-3 font-body text-xs text-muted-foreground uppercase">ROI</th>
                  <th className="text-left px-4 py-3 font-body text-xs text-muted-foreground uppercase">Featured</th>
                  <th className="text-right px-4 py-3 font-body text-xs text-muted-foreground uppercase">Actions</th>
                </tr>
              </thead>
              <tbody>
                {investments?.map((inv) => (
                  <tr key={inv.id} className="border-t border-border">
                    <td className="px-4 py-3 font-body text-sm text-foreground">{inv.title}</td>
                    <td className="px-4 py-3 font-body text-sm text-muted-foreground">{inv.location}</td>
                    <td className="px-4 py-3 font-body text-sm text-muted-foreground">{inv.type}</td>
                    <td className="px-4 py-3 font-body text-sm text-foreground">{inv.price}</td>
                    <td className="px-4 py-3 font-body text-sm text-foreground">{inv.roi}</td>
                    <td className="px-4 py-3 font-body text-sm">{inv.is_featured ? "⭐" : "—"}</td>
                    <td className="px-4 py-3 text-right">
                      <button onClick={() => { setEditing(inv); setShowForm(true); }} className="text-accent hover:text-accent/80 mr-3"><Edit className="w-4 h-4" /></button>
                      <button onClick={() => handleDelete(inv.id)} className="text-destructive hover:text-destructive/80"><Trash2 className="w-4 h-4" /></button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminPage;
